import React, { useContext, useState } from 'react';
import { CYCLE_LABEL, SELECTABLE_GRAD_YEARS } from '../../config/recruitingCycle';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import { buildQuestPlan } from '../../utils/questPlan';

/**
 * Offers a user whose quest belongs to a finished cycle a one-click renewal.
 *
 * Their previous answers (graduation year, hours per week, email preference)
 * are reused, so renewing does not mean walking the four-step accept flow
 * again. The old quest is archived server-side rather than overwritten, so the
 * tasks they completed last cycle are not lost.
 */
const QuestRenewalBanner = () => {
  const { userProfile, questPlan, nodes, completedNodes, fetchQuest, showToastMessage } =
    useContext(GlobalContext);
  const [renewing, setRenewing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState('');

  if (dismissed || !questPlan?.quest_id) return null;

  // A graduation year from a past cycle is no longer offered. Fall back to the
  // closest year still selectable rather than silently planning against one
  // that no longer maps to a cohort.
  const storedGradYear = questPlan.grad_year;
  const gradYear = SELECTABLE_GRAD_YEARS.includes(storedGradYear)
    ? storedGradYear
    : SELECTABLE_GRAD_YEARS.reduce((best, y) =>
        Math.abs(y - storedGradYear) < Math.abs(best - storedGradYear) ? y : best
      );
  const gradYearChanged = gradYear !== storedGradYear;

  const handleRenew = async () => {
    setRenewing(true);
    setError('');

    const hoursPerWeek = questPlan.hours_per_week ?? 5;
    const plan = buildQuestPlan({ gradYear, hoursPerWeek, nodes, completedNodes });

    if (plan.length === 0) {
      setError('No weeks remain in this cycle, so there is nothing to plan yet.');
      setRenewing(false);
      return;
    }

    const formatted = plan.map((week) => ({
      ...week,
      week_start:
        typeof week.week_start === 'string'
          ? week.week_start.split('T')[0]
          : week.week_start.toISOString().split('T')[0],
    }));

    try {
      await axios.post('/api/v1/quest/renew', {
        quest: {
          user_id: userProfile.user_id,
          hours_per_week: hoursPerWeek,
          grad_year: gradYear,
          start_date: formatted[0].week_start,
          quest_plan: formatted,
          allow_emails: questPlan.allow_emails ?? true,
        },
      });
      await fetchQuest?.();
      showToastMessage?.(`Your ${CYCLE_LABEL} quest is ready!`);
      setDismissed(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start your new quest. Please try again.');
      setRenewing(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            🗺️ Your quest was for a past recruiting season
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            Start your {CYCLE_LABEL} quest to get a fresh weekly plan. We&apos;ll reuse your
            answers, and everything you completed last season is kept.
          </p>
          {gradYearChanged && (
            <p className="mt-1 text-xs text-gray-600 italic">
              Your graduation year ({storedGradYear}) is no longer part of this cycle, so the plan
              will use {gradYear}. You can change it later.
            </p>
          )}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={handleRenew}
            disabled={renewing}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-transform ${
              renewing
                ? 'cursor-not-allowed bg-gray-400'
                : 'bg-gradient-to-r from-blue-500 to-pink-400 hover:scale-105'
            }`}
          >
            {renewing ? 'Building your plan…' : `Start my ${CYCLE_LABEL} quest`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestRenewalBanner;
